import { hexToRGB } from "@shared/utils/misc";
import React, { MouseEvent } from "react";
import styled, { css, useTheme } from "styled-components";
import { ChevronRight, Sparkles, Zap } from "lucide-react";
import IconWithFallback from "Global/IconWithFallback";
import { EXCHANGES, ICON_TYPES } from "@shared/common/constants";
import Tooltip from "common/Tooltips/Tooltip";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import Pill from "common/NewLook/Pill.tsx";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import { CREATE_ACCOUNT_EXCHANGE_CARD_ID } from "testIds.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import {
  focusRing,
  LogoTile,
} from "features/portfolio/CreateAccount/NewLook/styles";

interface NewLookExchangeCardProps {
  primaryExchange: string;
  tags?: string[];
  onSelectExchange?: (primaryExchange: string) => void;
  isPremiumIncluded?: boolean;
  isQuickConnect?: boolean;
  /** "featured": a larger card for the popular exchanges; "row": compact. */
  variant?: "featured" | "row" | "tile";
}

const FEATURED_LOGO = 32;
const ROW_LOGO = 28;
const TILE_LOGO = 36;

const NewLookExchangeCard = ({
  primaryExchange,
  tags,
  onSelectExchange,
  isPremiumIncluded,
  isQuickConnect,
  variant = "row",
}: NewLookExchangeCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const theme = useTheme();
  const stopPropagation = (event: MouseEvent) => event.stopPropagation();
  const isFeatured = variant === "featured";
  const isTile = variant === "tile";
  const logoSize = isFeatured ? FEATURED_LOGO : isTile ? TILE_LOGO : ROW_LOGO;
  const iconSize = logoSize - 2;

  return (
    <Card
      type="button"
      $featured={isFeatured}
      $tile={isTile}
      $highlight={!!isPremiumIncluded}
      onClick={() => onSelectExchange?.(primaryExchange)}
      data-testid={`${CREATE_ACCOUNT_EXCHANGE_CARD_ID}-${primaryExchange.toLowerCase()}`}
    >
      <LogoTile $size={logoSize}>
        <IconWithFallback
          iconType={ICON_TYPES.EXCHANGE}
          coinOrExchangeName={primaryExchange}
          pxIconSize={iconSize}
          iconStyle={{
            width: iconSize,
            height: iconSize,
            filter:
              primaryExchange === EXCHANGES.OKX && theme.isDarkMode
                ? "invert(1)"
                : undefined,
          }}
        />
      </LogoTile>
      <Body $featured={isFeatured}>
        <NameRow>
          <Name $tile={isTile}>{primaryExchange}</Name>
          {isQuickConnect && (
            <Pill tone="neutral" icon={Zap}>
              {t("create_account.new_look.quick_connect_tag")}
            </Pill>
          )}
        </NameRow>
        {!!tags?.length && <Markets $tile={isTile}>{tags.join(" · ")}</Markets>}
      </Body>
      {/* Production's pattern, calmer: a soft tag sitting on the card's
          bottom edge, with room for a full phrase. */}
      {isPremiumIncluded && (
        <EdgeTag>
          <Tooltip
            title={t("create_account.premium_plan_included_tooltip")}
            showMobileBottomDrawer={true}
            mobileTitle={t("create_account.new_look.premium_included")}
            placement="bottom"
          >
            <PremiumNote onClick={stopPropagation}>
              <Sparkles size={12} strokeWidth={2} />
              {t("create_account.new_look.premium_included")}
            </PremiumNote>
          </Tooltip>
        </EdgeTag>
      )}
      {!isFeatured && (
        <Chevron aria-hidden={true}>
          <ChevronRight size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
        </Chevron>
      )}
    </Card>
  );
};

export default React.memo(NewLookExchangeCard);

const Card = styled.button<{
  $featured: boolean;
  $tile?: boolean;
  $highlight?: boolean;
}>`
  position: relative;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
  margin: 0;
  font: inherit;
  text-align: left;
  color: inherit;
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease;
  ${focusRing};
  &:focus-visible {
    outline-offset: -2px;
  }
  ${(p) => {
    const tokens = getNewLookTokens(p.theme);
    // A bordered horizontal card: the onboarding page's uniform grid. It
    // lifts slightly on hover (the chevron appears too).
    if (p.$tile)
      return css`
        min-height: 80px;
        gap: 14px;
        padding: 16px 16px 16px 18px;
        border: 1px solid ${tokens.hairline};
        border-radius: ${NEW_LOOK_RADIUS.panel}px;
        background: ${tokens.panel};
        transition: border-color 150ms ease, box-shadow 180ms ease,
          transform 180ms ease;
        &:hover {
          border-color: ${tokens.hairlineStrong};
          box-shadow: ${p.theme.isDarkMode
            ? "0 6px 18px rgba(0, 0, 0, 0.32)"
            : "0 1px 2px rgba(16, 24, 40, 0.04), 0 6px 18px rgba(16, 24, 40, 0.07)"};
          transform: translateY(-1px);
        }
        &:active {
          transform: none;
        }
        ${p.$highlight &&
        css`
          &,
          &:hover {
            border-color: ${hexToRGB(p.theme.colors.primaryBlue, 0.45)};
          }
          /* Room for the tag on the bottom edge, in this row only. */
          margin-bottom: 8px;
        `}
        @media (prefers-reduced-motion: reduce) {
          transition: border-color 150ms ease, box-shadow 150ms ease;
          &:hover {
            transform: none;
          }
        }
        @media (max-width: 559px) {
          min-height: 72px;
          padding: 14px 14px 14px 16px;
        }
      `;
    return p.$featured
      ? css`
          flex-direction: column;
          align-items: flex-start;
          gap: 16px;
          padding: 16px;
          border: 1px solid ${tokens.hairline};
          border-radius: ${NEW_LOOK_RADIUS.panel}px;
          background: ${tokens.panel};
          &:hover {
            background: ${tokens.rowHover};
            border-color: ${tokens.hairlineStrong};
          }
          @container (max-width: 479px) {
            flex-direction: row;
            align-items: center;
            padding: 12px 16px;
          }
        `
      : css`
          min-height: 52px;
          padding: 8px 12px;
          border: 0;
          border-radius: ${NEW_LOOK_RADIUS.control}px;
          background: transparent;
          &:hover {
            background: ${tokens.rowHover};
          }
        `;
  }}
`;
const Body = styled.span<{ $featured?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
  ${(p) => p.$featured && "width: 100%;"}
`;
const NameRow = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  min-width: 0;
  > span:last-child:not(:first-child) {
    flex: none;
  }
`;
const Chevron = styled.span`
  flex: none;
  display: inline-flex;
  color: ${(p) => getNewLookTokens(p.theme).muted};
  opacity: 0;
  transform: translateX(-4px);
  transition: opacity 150ms ease, transform 150ms ease;
  ${Card}:hover &,
  ${Card}:focus-visible & {
    opacity: 1;
    transform: none;
  }
  @media (hover: none) {
    opacity: 1;
    transform: none;
  }
`;
const Name = styled.span<{ $tile?: boolean }>`
  ${newLookType.body};
  font-weight: 560;
  letter-spacing: -0.005em;
  ${(p) =>
    p.$tile &&
    css`
      font-size: 15px;
      line-height: 22px;
      font-weight: 520;
      letter-spacing: -0.01em;
    `}
  color: ${(p) => getNewLookTokens(p.theme).ink};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Markets = styled.span<{ $tile?: boolean }>`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  ${(p) =>
    p.$tile &&
    css`
      font-size: 12.5px;
      line-height: 18px;
      color: ${getNewLookTokens(p.theme).muted};
    `}
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const EdgeTag = styled.span`
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translate(-50%, 50%);
  display: flex;
  z-index: 1;
`;
const PremiumNote = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 11px;
  border-radius: 999px;
  background: ${(p) => p.theme.colors.lightBlue};
  box-shadow: 0 0 0 2px ${(p) => p.theme.colors.cardBgColor};
  font-size: 11.5px;
  line-height: 16px;
  font-weight: 500;
  white-space: nowrap;
  color: ${(p) => p.theme.colors.primaryBlue};
  cursor: help;
`;
